import { useLocation } from 'react-router';

import type { CSVTableMetadata } from '@/components/CsvTableWidget';
import CsvTableWidget from '@/components/CsvTableWidget';
import FilesWidget from '@/components/FilesWidget';
import { Permission } from '@/entities/permissions';
import PremiumWidgetDenied from '@/features/Dashboard/components/PremiumWidgetDenied';
import ChartsBlock from '@/pages/Markets/MarketsComponents/Chart';
import CommoditiesTable from '@/pages/Markets/MarketsComponents/CommoditiesTable';
import Index from '@/pages/Markets/MarketsComponents/components/CustomChart';
import { Feed } from '@/pages/Markets/MarketsComponents/Feed';
import type { FeedFilters } from '@/pages/Markets/MarketsComponents/Feed/api/fetchFeedData';
import MiniTablesContainer from '@/pages/Markets/MarketsComponents/MiniTablesContainer';
import MultiGroupTable from '@/pages/Markets/MarketsComponents/MultiGroupTable';
import SpotHistoryTable from '@/pages/Markets/MarketsComponents/SpotHistoryTable';
import usePermission from '@/shared/hooks/usePermission';
import type { BaseTabType } from '@/types/markets';

type NumericLayout = { fullWidth?: boolean; height?: number };

function normalizeLayout(layout: BaseTabType['layout']): NumericLayout | undefined {
  if (!layout) return undefined;

  const height = Number(layout.height);
  const fullWidth =
    typeof layout.fullWidth === 'string' ? layout.fullWidth === 'true' : layout.fullWidth;

  return {
    ...(typeof fullWidth === 'boolean' ? { fullWidth } : {}),
    ...(Number.isFinite(height) && height > 0 ? { height } : {})
  };
}

export const MarketWidget = ({ tab }: { tab: BaseTabType }) => {
  const { hasPermission } = usePermission();
  const location = useLocation();
  const layout = normalizeLayout(tab.layout);

  const isPremiumWidgetPermissionGranted =
    hasPermission(Permission.DashboardPremiumWidgets) || !location.pathname.includes('dashboard');

  switch (tab.type) {
    case 'table':
      return (
        <CommoditiesTable
          key={tab.id}
          layout={layout}
          sections={tab.sections}
        />
      );
    case 'multi_group_table':
      return (
        <MultiGroupTable
          key={tab.id}
          layout={layout}
          sections={tab.sections}
        />
      );
    case 'graph':
      if (!isPremiumWidgetPermissionGranted) {
        return <PremiumWidgetDenied />;
      }
      if (tab.metadata?.graphType === 'custom') {
        return (
          <Index
            key={tab.id}
            layout={layout}
            metadata={tab.metadata}
          />
        );
      }
      return (
        <ChartsBlock
          key={tab.id}
          layout={layout}
          tab={tab}
        />
      );

    case 'feed':
      return (
        <Feed
          key={tab.id}
          metadata={tab.metadata as FeedFilters}
        />
      );

    case 'minitable':
      return <MiniTablesContainer tables={tab.tables} />;
    case 'spot_history_table':
      return (
        <SpotHistoryTable
          key={tab.id}
          columns={tab.columns}
          layout={layout}
          rows={tab.rows}
        />
      );
    case 'csvtable':
      return (
        <CsvTableWidget
          fileName={tab.fileName}
          groupName={tab.groupName}
          layout={layout}
          metadata={tab.metadata as CSVTableMetadata}
        />
      );
    case 'file_list':
      return (
        <FilesWidget
          downloadable={tab.metadata?.downloadable}
          groupName={tab.groupName}
          size={tab.size}
        />
      );
    default:
      return null;
  }
};
